import React, { useState, useRef } from 'react';
import axios from 'axios';
import { FiImage, FiUpload, FiTrash2, FiAlertCircle, FiFileText, FiTag, FiBook, FiClipboard, FiZap, FiCheckCircle } from 'react-icons/fi';

const CASE_STUDIES_API_URL = `${process.env.REACT_APP_API_URL}/api/case-studies`;

const inputClass = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors';
const selectClass = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-white';
const textareaClass = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

function CaseStudyForm({ onSave, onCancel, initialData }) {
  const [form, setForm] = useState(initialData || {
    title: '',
    category: 'General',
    description: '',
    case_background: '',
    legal_issue: '',
    solution_provided: '',
    outcome: '',
    featured_image: '',
    tags: '',
    status: 'draft'
  });

  const [imagePreview, setImagePreview] = useState(initialData?.featured_image || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const categories = [
    'General',
    'Criminal Law',
    'Family Law',
    'Corporate Law',
    'Real Estate',
    'Immigration Law',
    'Personal Injury',
    'Contract Law',
    'Civil Litigation'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTextAreaChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'].includes(file.type)) {
        setError('Only image files are allowed');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
        setForm(prev => ({
          ...prev,
          featured_image: file
        }));
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.title.trim()) {
      setError('Please enter a title');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const userData = JSON.parse(localStorage.getItem('user_data'));
      const adminId = userData?.id;

      if (!adminId) {
        setError('Admin ID not found');
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('title', form.title);
      formDataToSend.append('category', form.category);
      formDataToSend.append('description', form.description);
      formDataToSend.append('case_background', form.case_background);
      formDataToSend.append('legal_issue', form.legal_issue);
      formDataToSend.append('solution_provided', form.solution_provided);
      formDataToSend.append('outcome', form.outcome);
      formDataToSend.append('tags', form.tags);
      formDataToSend.append('status', form.status);
      formDataToSend.append('admin_id', adminId);

      if (form.featured_image instanceof File) {
        formDataToSend.append('featured_image', form.featured_image);
      }

      const endpoint = initialData?.id 
        ? `${CASE_STUDIES_API_URL}/manage.php?id=${initialData.id}`
        : `${CASE_STUDIES_API_URL}/manage.php`;

      const method = initialData?.id ? 'PUT' : 'POST';

      const response = await axios({
        method,
        url: endpoint,
        data: formDataToSend,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        onSave();
      } else {
        setError(response.data.message || 'Failed to save case study');
      }
    } catch (err) {
      console.error('Error saving case study:', err);
      setError(err.response?.data?.message || 'Error saving case study');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div>
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2 mb-4">
          <FiFileText className="w-4 h-4" /> Basic Information
        </h3>

        {error && (
          <div className="flex items-center gap-2.5 p-3 bg-red-50 border border-red-100 text-red-700 rounded-xl text-sm mb-4">
            <FiAlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="title" className={labelClass}>Case Title *</label>
            <input
              id="title"
              type="text"
              name="title"
              value={form.title}
              onChange={handleInputChange}
              placeholder="Enter case title"
              required
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className={labelClass}>Category *</label>
              <select
                id="category"
                name="category"
                value={form.category}
                onChange={handleInputChange}
                className={selectClass}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="status" className={labelClass}>Status</label>
              <select
                id="status"
                name="status"
                value={form.status}
                onChange={handleInputChange}
                className={selectClass}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          {/* Featured Image Upload */}
          <div>
            <label className={labelClass}>Featured Image</label>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="w-full sm:w-48 h-32 rounded-xl overflow-hidden bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-gray-300">
                    <FiImage className="w-8 h-8" />
                    <span className="text-xs">No image</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary bg-primary/5 hover:bg-primary/10 rounded-xl transition-colors"
                >
                  <FiUpload className="w-4 h-4" /> Upload Image
                </button>
                {imagePreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setImagePreview(null);
                      setForm(prev => ({ ...prev, featured_image: '' }));
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4" /> Remove
                  </button>
                )}
                <p className="text-xs text-gray-400">Max 5MB. JPEG, PNG, GIF, or WebP format.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Case Details */}
      <div>
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2 mb-4">
          <FiBook className="w-4 h-4" /> Case Details
        </h3>

        <div className="space-y-4">
          <div>
            <label htmlFor="description" className={labelClass}>Case Summary</label>
            <textarea
              id="description"
              name="description"
              value={form.description}
              onChange={handleTextAreaChange}
              placeholder="Provide a brief summary of the case..."
              rows="4"
              className={textareaClass}
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{form.description.length} / 1000</p>
          </div>

          <div>
            <label htmlFor="case_background" className={labelClass}>
              <span className="flex items-center gap-1.5"><FiClipboard className="w-3.5 h-3.5 text-primary" /> Case Background</span>
            </label>
            <textarea
              id="case_background"
              name="case_background"
              value={form.case_background}
              onChange={handleTextAreaChange}
              placeholder="Describe the background and circumstances of the case..."
              rows="5"
              className={textareaClass}
            />
          </div>

          <div>
            <label htmlFor="legal_issue" className={labelClass}>
              <span className="flex items-center gap-1.5"><FiAlertCircle className="w-3.5 h-3.5 text-amber-500" /> Legal Issue / Challenge</span>
            </label>
            <textarea
              id="legal_issue"
              name="legal_issue"
              value={form.legal_issue}
              onChange={handleTextAreaChange}
              placeholder="What was the main legal issue or challenge?"
              rows="4"
              className={textareaClass}
            />
          </div>

          <div>
            <label htmlFor="solution_provided" className={labelClass}>
              <span className="flex items-center gap-1.5"><FiZap className="w-3.5 h-3.5 text-blue-500" /> Solution Provided</span>
            </label>
            <textarea
              id="solution_provided"
              name="solution_provided"
              value={form.solution_provided}
              onChange={handleTextAreaChange}
              placeholder="Describe the solution and strategy implemented..."
              rows="5"
              className={textareaClass}
            />
          </div>

          <div>
            <label htmlFor="outcome" className={labelClass}>
              <span className="flex items-center gap-1.5"><FiCheckCircle className="w-3.5 h-3.5 text-green-500" /> Outcome & Results</span>
            </label>
            <textarea
              id="outcome"
              name="outcome"
              value={form.outcome}
              onChange={handleTextAreaChange}
              placeholder="What was the outcome? Include specific results and benefits..."
              rows="4"
              className={textareaClass}
            />
          </div>
        </div>
      </div>

      {/* Tags & Metadata */}
      <div>
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2 mb-4">
          <FiTag className="w-4 h-4" /> Tags & Metadata
        </h3>

        <div>
          <label htmlFor="tags" className={labelClass}>Tags (comma-separated)</label>
          <input
            id="tags"
            type="text"
            name="tags"
            value={form.tags}
            onChange={handleInputChange}
            placeholder="e.g., Contract Negotiation, Settlement, Corporate"
            className={inputClass}
          />
          <p className="text-xs text-gray-400 mt-1">Separate tags with commas</p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-light rounded-xl transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : initialData?.id ? 'Update Case Study' : 'Create Case Study'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default CaseStudyForm;
